import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import MapView from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Upload, Trash2, Layers, Loader2 } from "lucide-react";

const VIRIDIS = "linear-gradient(90deg,#440154,#482878,#3e4a89,#31688e,#26828e,#1f9e89,#35b779,#6ece58,#b5de2b,#fde725)";
const fmtVal = (v) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "-";
  const a = Math.abs(v);
  return a >= 100 ? v.toFixed(0) : a >= 1 ? v.toFixed(2) : v.toFixed(3);
};

// Optional user GeoTIFF overlay - additive across all 3 modes. When no file is
// uploaded, the automated Sentinel-2 / GIS pipelines are used (unchanged).
export function GeoTiffLayer({ pid, boundary = null, title = "Reference GeoTIFF Layer" }) {
  const [overlay, setOverlay] = useState(null);
  const [opacity, setOpacity] = useState(0.75);
  const [visible, setVisible] = useState(true);
  const [busy, setBusy] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!pid) return;
    api.get(`/projects/${pid}/geotiff`).then(({ data }) => setOverlay(data.overlay || null)).catch(() => {});
  }, [pid]);

  const upload = async (file) => {
    if (!file) return;
    setBusy("up");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/projects/${pid}/geotiff/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOverlay(data.overlay); setVisible(true);
      toast.success("GeoTIFF added as an overlay layer");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); if (inputRef.current) inputRef.current.value = ""; }
  };

  const remove = async () => {
    setBusy("rm");
    try { await api.delete(`/projects/${pid}/geotiff`); setOverlay(null); toast.success("GeoTIFF overlay removed"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6" data-testid="geotiff-layer">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <span className="h-9 w-9 rounded-md text-white grid place-items-center shrink-0" style={{ backgroundColor: "#C20511" }}><Layers size={18} /></span>
          <div>
            <div className="text-sm font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>{title}</div>
            <div className="text-[11px] text-slate-400">Optional - overlay your own GeoTIFF. If skipped, automated satellite / GIS sources are used.</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" accept=".tif,.tiff" className="hidden" data-testid="geotiff-input" onChange={(e) => upload(e.target.files?.[0])} />
          <Button data-testid="geotiff-upload-btn" onClick={() => inputRef.current?.click()} disabled={busy === "up"} variant="outline" className="border-slate-300 h-9">
            {busy === "up" ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Upload size={15} className="mr-2" />} {overlay ? "Replace" : "Upload GeoTIFF"}
          </Button>
          {overlay && (
            <Button data-testid="geotiff-remove-btn" onClick={remove} disabled={busy === "rm"} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 h-9">
              <Trash2 size={15} />
            </Button>
          )}
        </div>
      </div>

      {overlay ? (
        <>
          <div className="flex items-center gap-4 flex-wrap mb-3 text-xs text-slate-500">
            <label className="flex items-center gap-2"><input type="checkbox" data-testid="geotiff-visible" checked={visible} onChange={(e) => setVisible(e.target.checked)} className="h-4 w-4 accent-[#C20511]" /> Show layer</label>
            <div className="flex items-center gap-2 min-w-[180px] flex-1 max-w-xs">
              <span>Opacity</span>
              <Slider data-testid="geotiff-opacity" value={[Math.round(opacity * 100)]} min={10} max={100} step={5} onValueChange={(v) => setOpacity(v[0] / 100)} />
              <span className="tabular-nums w-9">{Math.round(opacity * 100)}%</span>
            </div>
            <span className="text-slate-400 truncate" data-testid="geotiff-meta">{overlay.name} · {overlay.bands} band{overlay.bands === 1 ? "" : "s"} · {overlay.crs}</span>
          </div>
          <div className="relative rounded-lg overflow-hidden border border-slate-200" style={{ height: 340 }} data-testid="geotiff-map">
            <MapView boundary={boundary} bbox={overlay.bbox} basemap="satellite"
              overlay={visible ? { png: overlay.png, bbox: overlay.bbox } : null} overlayOpacity={opacity} />
            {visible && (
              <div className="absolute z-[500] bottom-3 left-3 bg-white/90 backdrop-blur rounded-md shadow-md px-3 py-2 text-[10px] text-slate-700" data-testid="geotiff-legend">
                {overlay.single_band ? (
                  <>
                    <div className="font-semibold mb-1 text-slate-800">Value scale</div>
                    <div className="h-2.5 w-40 rounded-sm" style={{ background: VIRIDIS }} role="img" aria-label="Low to high value colour scale" />
                    <div className="flex justify-between w-40 mt-0.5 tabular-nums">
                      <span data-testid="geotiff-legend-min">{fmtVal(overlay.value_min)}</span>
                      <span data-testid="geotiff-legend-max">{fmtVal(overlay.value_max)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm" style={{ background: "linear-gradient(135deg,#c1440e,#2a6f97,#e9c46a)" }} />
                    True-colour composite (RGB)
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-10 text-center text-sm text-slate-400" data-testid="geotiff-empty">
          No GeoTIFF uploaded - the automated Sentinel-2 / GIS sources are being used.
        </div>
      )}
    </section>
  );
}
